// src/pages/SalesList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFilter,
  FaTimesCircle,
  FaFileExcel,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight, FiRotateCcw } from "react-icons/fi";
import { formatDate } from "../config/helperFunctions";
import {
  useGetInvoicesQuery,
  useExportInvoicesExcelMutation,
} from "../toolkitServices/invoiceApi";
import { openInvoicePdfInNewTab } from "../utils/receipts";
import toast from "react-hot-toast";
import Select from "react-select";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(148,129,206,.25)" : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/* ------------ download helpers ------------ */
const getFileNameFromDisposition = (disposition, fallback) => {
  if (!disposition) return fallback;
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disposition);
  return decodeURIComponent((m?.[1] || fallback).trim());
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const SalesReport = () => {
  const navigate = useNavigate();

  // --------- Filters & paging ----------
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // react-select selected values
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // debounced search (client-side only)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ✅ modals
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  // ESC close for modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsFilterOpen(false);
        setIsSummaryOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // --------- Fetch invoices ----------
  const queryArgs = {
    page,
    limit,
    branchId,
    customerId,
    from,
    to,
    search: debouncedSearch, // client side
    q: "", // backend invCode search if needed
    sortBy: "invNo",
    sortOrder: "desc",
  };

  const { data, isLoading, isFetching, error } = useGetInvoicesQuery(queryArgs, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const [exportInvoicesExcel, exportState] = useExportInvoicesExcelMutation();

  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total ?? data?.pagination?.totalDocs ?? rows.length;
  const totalPages =
    data?.pages ??
    data?.pagination?.totalPages ??
    Math.max(
      1,
      Math.ceil((total || 0) / (data?.limit || limit || 10))
    );
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // ✅ Summary objects from API
  const summary = data?.summary || null; // all invoices (filters wise)
  const pageSummary = data?.pageSummary || null; // current page totals
  const hasSummary = !!(summary || pageSummary);

  const fmt = (n, d = 3) => (Number(n || 0)).toFixed(d);

  const summaryCards = useMemo(() => {
    const s = summary || {};
    const p = pageSummary || {};
    return [
      {
        key: "qty",
        label: "Qty",
        allVal: summary ? fmt(s.qty, 0) : "—",
        pageVal: pageSummary ? fmt(p.qty, 0) : "—",
      },
      {
        key: "totalWt",
        label: "Total Wt",
        allVal: summary ? fmt(s.totalWt) : "—",
        pageVal: pageSummary ? fmt(p.totalWt) : "—",
      },
      {
        key: "packingWt",
        label: "Packing Wt",
        allVal: summary ? fmt(s.packingWt) : "—",
        pageVal: pageSummary ? fmt(p.packingWt) : "—",
      },
      {
        key: "grossWt",
        label: "Gross Wt",
        allVal: summary ? fmt(s.grossWt) : "—",
        pageVal: pageSummary ? fmt(p.grossWt) : "—",
      },
      {
        key: "stoneWt",
        label: "Stone Wt",
        allVal: summary ? fmt(s.stoneWt) : "—",
        pageVal: pageSummary ? fmt(p.stoneWt) : "—",
      },
      {
        key: "diamondWt",
        label: "Diamond Wt",
        allVal: summary ? fmt(s.diamondWt) : "—",
        pageVal: pageSummary ? fmt(p.diamondWt) : "—",
      },
      {
        key: "netWt",
        label: "Net Wt",
        allVal: summary ? fmt(s.netWt) : "—",
        pageVal: pageSummary ? fmt(p.netWt) : "—",
      },
      {
        key: "stonePcs",
        label: "Stone Pcs",
        allVal: summary ? fmt(s.stonePcs, 0) : "—",
        pageVal: pageSummary ? fmt(p.stonePcs, 0) : "—",
      },
      {
        key: "diamondPcs",
        label: "Diamond Pcs",
        allVal: summary ? fmt(s.diamondPcs, 0) : "—",
        pageVal: pageSummary ? fmt(p.diamondPcs, 0) : "—",
      },
    ];
  }, [summary, pageSummary]);

  // client-side sort + search
  const sortedRows = useMemo(() => {
    const list = Array.isArray(rows) ? [...rows] : [];
    list.sort((a, b) => {
      const aNo = Number(a?.invNo ?? a?.invCode ?? 0);
      const bNo = Number(b?.invNo ?? b?.invCode ?? 0);
      return bNo - aNo;
    });
    return list;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return sortedRows;
    const q = debouncedSearch.toLowerCase();
    return sortedRows.filter((inv) => {
      const hay = [
        String(inv?.invNo ?? ""),
        inv?.invCode,
        inv?.notes,
        inv?.customerName,
        inv?.customerCode,
        inv?.customerId?.customerName,
        inv?.customerId?.customerId,
        inv?.branchName,
        inv?.branchId?.branchName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sortedRows, debouncedSearch]);

  // Keyboard: Ctrl+P -> create sale
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/sales/sales-list/create-sale");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // ✅ whenever filters change, go to page 1
  useEffect(() => {
    setPage(1);
  }, [branchId, customerId, from, to, debouncedSearch, limit]);

  // Print/View PDF
  const handlePrint = (id) => {
    if (!id) return;
    const tab = window.open("about:blank", "_blank");
    if (!tab) {
      toast.error("Popup blocked. Allow popups to print.");
      return;
    }
    openInvoicePdfInNewTab(id, { openedTab: tab });
  };

  // ✅ Export Excel (BLOB)
  const handleExportExcel = async () => {
    try {
      // NOTE: export params are server-side (search is client-side only)
      const args = {
        branchId,
        customerId,
        from,
        to,
        sortBy: "invNo",
        sortOrder: "desc",
      };

      const res = await exportInvoicesExcel(args);

      if (res?.error) {
        throw new Error(
          res.error?.data?.message || res.error?.error || "Failed to export excel"
        );
      }

      const blob = res?.data;
      if (!(blob instanceof Blob)) throw new Error("Invalid excel response");

      const disposition =
        res?.meta?.response?.headers?.get?.("content-disposition");
      const fallbackName = `invoices_${Date.now()}.xlsx`;
      const fileName = getFileNameFromDisposition(disposition, fallbackName);

      downloadBlob(blob, fileName);
      toast.success("Excel downloaded");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Excel download failed");
    }
  };

  // react-select styling
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 34,
      height: 34,
      borderRadius: 10,
      borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 3px #7c3aed22" : "none",
      "&:hover": { borderColor: "#7c3aed" },
      fontSize: "0.8rem",
      backgroundColor: "white",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    singleValue: (base) => ({
      ...base,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#111827",
    }),
    placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
    indicatorsContainer: (base) => ({ ...base, height: 34 }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.8rem",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      backgroundColor: state.isFocused
        ? "#ede9fe"
        : state.isSelected
        ? "#ddd6fe"
        : "white",
      color: "#111827",
    }),
    menu: (base) => ({ ...base, zIndex: 40 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ✅ chips
  const chips = useMemo(() => {
    const arr = [];
    if (search?.trim()) arr.push({ key: "search", label: `Search: ${search.trim()}` });
    if (selectedBranch?.label) arr.push({ key: "branch", label: `Branch: ${selectedBranch.label}` });
    if (selectedCustomer?.label) arr.push({ key: "customer", label: `Customer: ${selectedCustomer.label}` });
    if (from) arr.push({ key: "from", label: `From: ${from}` });
    if (to) arr.push({ key: "to", label: `To: ${to}` });
    return arr;
  }, [search, selectedBranch, selectedCustomer, from, to]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    if (key === "branch") {
      setSelectedBranch(null);
      setBranchId("");
    }
    if (key === "customer") {
      setSelectedCustomer(null);
      setCustomerId("");
    }
    if (key === "from") setFrom("");
    if (key === "to") setTo("");
  };

  const resetFilters = () => {
    setSearch("");
    setSelectedBranch(null);
    setBranchId("");
    setSelectedCustomer(null);
    setCustomerId("");
    setFrom("");
    setTo("");
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* 🔄 Global overlay loader */}
      {isLoading && !rows.length && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/60">
          <div className="flex flex-col items-center gap-2 text-slate-600 text-xs sm:text-sm">
            <Spinner size={22} />
            <span>Loading invoices…</span>
          </div>
        </div>
      )}

      {/* Top header bar */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">
            Invoice List
          </h2>
          {isFetching && rows.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>

          {/* ✅ Excel Download */}
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exportState?.isLoading}
            className="inline-flex items-center gap-1 rounded-full bg-white text-emerald-700 px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-emerald-50 transition disabled:opacity-50"
            title="Download Excel"
          >
            {exportState?.isLoading ? <Spinner size={12} /> : <FaFileExcel />}
            <span className="hidden sm:inline">Excel</span>
          </button>

          <Link
            to="/sales/sales-list/create-sale"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-slate-50/80">
        {/* MOBILE */}
        <div className="flex sm:hidden items-center gap-2">
          <input
            type="text"
            placeholder="Search invoice / customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
          />

          <button
            onClick={() => setIsSummaryOpen(true)}
            className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 flex-shrink-0 disabled:opacity-50"
            title="Summary"
            disabled={!hasSummary}
          >
            Summary
          </button>

          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 flex-shrink-0"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>
        </div>

        {/* DESKTOP */}
        <div className="hidden sm:flex flex-wrap gap-3 items-end">
          {/* Branch (same as your file - no change) */}
          <div className="flex flex-col gap-[1px] w-[190px]">
            <label className="text-[11px] font-medium text-slate-600">
              Branch
            </label>
            <Select
              isClearable
              isSearchable
              options={[]}
              value={selectedBranch}
              onChange={(opt) => {
                setSelectedBranch(opt);
                setBranchId(opt?.value || "");
              }}
              placeholder="All branches"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* Customer */}
          <div className="flex flex-col gap-[1px] w-[260px]">
            <label className="text-[11px] font-medium text-slate-600">
              Customer
            </label>
            <Select
              isClearable
              isSearchable
              options={[]}
              value={selectedCustomer}
              onChange={(opt) => {
                setSelectedCustomer(opt);
                setCustomerId(opt?.value || "");
              }}
              placeholder="All customers"
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* From date */}
          <div className="flex flex-col gap-[1px] w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">
              From
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* To date */}
          <div className="flex flex-col gap-[1px] w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Search */}
          <div className="flex flex-col gap-[1px] flex-1 min-w-[220px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="Invoice no, customer, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Summary + Filters (side-by-side) */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => setIsSummaryOpen(true)}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 disabled:opacity-50"
              disabled={!hasSummary}
              title="Summary"
            >
              Summary
            </button>

            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50"
              title="Filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* Errors */}
      {error && (
        <div className="px-3 sm:px-4 py-1.5 text-red-600 text-xs border-b bg-red-50">
          {error?.data?.message || "Failed to load invoices"}
        </div>
      )}

      {/* Chips */}
      {chips.length > 0 && (
        <div className="px-3 sm:px-4 py-1.5 border-b bg-slate-50 flex items-center gap-2">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0 pr-2">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm whitespace-nowrap flex-shrink-0"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline flex-shrink-0"
            onClick={resetFilters}
            title="Reset all"
          >
            Reset
          </button>
        </div>
      )}

      {/* Table */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[1120px] text-left text-xs sm:text-sm">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-[10px] sm:text-[11px] sticky top-0 z-10">
            <tr>
              <th className="min-w-[110px] whitespace-nowrap px-3 py-1.5 sm:px-4">
                Inv No
              </th>
              <th className="min-w-[180px] whitespace-nowrap px-3 py-1.5 sm:px-4">
                Customer
              </th>
              <th className="min-w-[150px] whitespace-nowrap px-3 py-1.5 sm:px-4">
                Branch
              </th>
              <th className="min-w-[120px] whitespace-nowrap px-3 py-1.5 sm:px-4">
                Date
              </th>
              <th className="min-w-[120px] whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                Sub Total
              </th>
              <th className="min-w-[100px] whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                GST
              </th>
              {/* <th className="px-3 sm:px-4 py-1.5">Discount</th> */}
              <th className="min-w-[120px] whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                Grand
              </th>
              <th className="min-w-[110px] whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                Paid
              </th>
              <th className="min-w-[130px] whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                Outstanding
              </th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {filteredRows?.length
                ? filteredRows.map((inv, index) => {
                    const id = inv?._id || inv?.id;
                    const invNo = inv?.invNo ?? inv?.invCode ?? "—";

                    const cust =
                      inv?.customerCode ||
                      inv?.customerName ||
                      inv?.customerId?.customerName ||
                      inv?.customerId?.customerId ||
                      "—";

                    const branch =
                      inv?.branchName ||
                      inv?.branchId?.branchName ||
                      inv?.branchId?.name ||
                      "—";

                    const dt =
                      inv?.invDate || inv?.date || inv?.createdAt || null;

                    const g =
                      Number(inv?.totals?.grandTotal ?? inv?.grandTotal ?? 0) ||
                      0;
                    const p = Number(inv?.totals?.paid ?? inv?.paid ?? 0) || 0;
                    const o =
                      Number(
                        inv?.totals?.outstanding ??
                          inv?.outstanding ??
                          Math.max(0, g - p)
                      ) || 0;

                    return (
                      <motion.tr
                        key={id || index}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, delay: index * 0.01 }}
                        className="border-t transition hover:bg-[#f4f0ff]"
                      >
                        <td className="px-3 py-2.5 font-medium sm:px-4">
                          {id ? (
                            <button
                              type="button"
                              className="text-[#6b59c6] underline underline-offset-2 hover:opacity-80"
                              title="View / Print invoice"
                              onClick={() => handlePrint(id)}
                            >
                              {invNo}
                            </button>
                          ) : (
                            invNo
                          )}
                        </td>

                        <td className="max-w-[180px] px-3 py-1.5 sm:px-4">
                          <span className="block truncate" title={cust}>
                            {cust}
                          </span>
                        </td>

                        <td className="max-w-[160px] px-3 py-1.5 sm:px-4">
                          <span className="block truncate" title={branch}>
                            {branch}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-3 py-1.5 sm:px-4">
                          {dt ? formatDate(dt) : "—"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                          ₹ {inv?.subTotal?.toFixed(2)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                          ₹ {inv?.gstAmount?.toFixed(2)}
                        </td>
                        {/* <td className="px-3 sm:px-4 py-1.5 text-right">
                          ₹ {inv?.discount?.toFixed(2)}
                        </td> */}
                        <td className="whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                          ₹ {g.toFixed(2)}
                        </td>

                        <td className="whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                          ₹ {p.toFixed(2)}
                        </td>

                        <td className="whitespace-nowrap px-3 py-1.5 text-right sm:px-4">
                          ₹ {o.toFixed(2)}
                        </td>
                      </motion.tr>
                    );
                  })
                : !isLoading && (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan="9" className="py-6 text-center text-gray-500">
                        No matching records found.
                      </td>
                    </motion.tr>
                  )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t bg-white px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden text-slate-600 sm:block">
          Total <span className="font-semibold">{total}</span> entries
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[10, 20, 50, 100].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:flex-none">
            <button
              type="button"
              disabled={!canPrev || isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <FiChevronLeft />
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from(
                    { length: Math.max(1, totalPages) },
                    (_, index) => ({
                      value: index + 1,
                      label: String(index + 1),
                    }),
                  )}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {Math.max(1, totalPages)}</span>
              </span>
              <span className="hidden sm:inline">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{Math.max(1, totalPages)}</span>
              </span>
            </span>

            <button
              type="button"
              disabled={!canNext || isFetching}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base disabled:opacity-40 sm:h-auto sm:w-auto sm:gap-1 sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="hidden sm:inline">Next</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Filters Modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsFilterOpen(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter invoices by branch, customer and date range.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      isClearable
                      isSearchable
                      options={[]}
                      value={selectedBranch}
                      onChange={(opt) => {
                        setSelectedBranch(opt);
                        setBranchId(opt?.value || "");
                      }}
                      placeholder="All branches"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">Customer</label>
                    <Select
                      isClearable
                      isSearchable
                      options={[]}
                      value={selectedCustomer}
                      onChange={(opt) => {
                        setSelectedCustomer(opt);
                        setCustomerId(opt?.value || "");
                      }}
                      placeholder="All customers"
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">From</label>
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">To</label>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Invoice no, customer, notes…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ Summary Modal (Desktop + Mobile) - NO SCROLL */}
      <AnimatePresence>
        {isSummaryOpen && (
          <motion.div
            className="fixed inset-0 z-[9999] bg-black/45 flex items-center justify-center p-3 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSummaryOpen(false)}
          >
            <motion.div
              className="w-full max-w-3xl rounded-3xl shadow-2xl overflow-hidden bg-white"
              initial={{ scale: 0.92, y: 18, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 18, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative px-4 sm:px-6 py-4 text-white bg-gradient-to-r from-[#6b59c6] via-[#7b66d6] to-[#9481ce]">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-white/30 blur-2xl" />
                  <div className="absolute -bottom-12 -right-12 w-44 h-44 rounded-full bg-white/30 blur-2xl" />
                </div>

                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm sm:text-base font-semibold tracking-tight">
                      Totals Summary
                    </div>
                    <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95 leading-4">
                      <span className="font-semibold text-white">All</span>{" "}
                      (filters wise) <span className="opacity-70">•</span>{" "}
                      <span className="font-semibold text-white/90">Page</span>{" "}
                      (current)
                    </div>
                  </div>

                  <button
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition"
                    onClick={() => setIsSummaryOpen(false)}
                    title="Close"
                  >
                    <FaTimesCircle />
                  </button>
                </div>
              </div>

              {/* Content (NO scroll) */}
              <div className="p-3 sm:p-5">
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {summaryCards.map((c) => (
                    <div
                      key={c.key}
                      className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-2.5 sm:px-3 py-2.5 sm:py-3 shadow-sm"
                    >
                      <div className="text-[10px] sm:text-[11px] font-semibold text-slate-600 truncate">
                        {c.label}
                      </div>

                      <div className="mt-1 grid grid-cols-2 gap-2 items-end">
                        <div>
                          <div className="text-[9px] sm:text-[10px] text-slate-400">
                            All
                          </div>
                          <div className="text-[12px] sm:text-sm font-extrabold text-slate-900 leading-4">
                            {c.allVal}
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="text-[9px] sm:text-[10px] text-slate-400">
                            Page
                          </div>
                          <div className="text-[12px] sm:text-sm font-extrabold text-[#6b59c6] leading-4">
                            {c.pageVal}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] sm:text-[11px] text-slate-500">
                    Tip: ESC to close
                  </div>

                  <button
                    onClick={() => setIsSummaryOpen(false)}
                    className="px-3 sm:px-4 py-1.5 rounded-full bg-[#6b59c6] text-white text-[11px] font-semibold hover:opacity-90 transition"
                  >
                    Done
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesReport;
